import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../cn";

/**
 * A clickable column header for a sortable list, rendered as a single
 * `<button>` so the label and its sort icon are one hit target and Enter/Space
 * work natively (no `role="button"` + manual key handling needed).
 *
 * Deliberately not built on `Th` (`./Table.tsx`): this primitive renders
 * *inside* whatever header cell the caller already has — a real `<th>`, or
 * (as in the admin-v2 dossiers overview) a `role="columnheader"` `<span>` in
 * a CSS-grid table — so it composes with either without assuming a `<table>`
 * layout.
 *
 * Callers own `aria-sort` on the enclosing header cell: WAI-ARIA defines that
 * attribute on the header element itself (`columnheader`/`th[scope=col]`), not
 * on the button inside it.
 */
export interface SortableColumnHeaderProps {
  /** The column's visible label, e.g. "Bestuurder". */
  readonly children: ReactNode;
  /**
   * `null` when this is not the active sort column (renders the subtle,
   * always-visible up/down icon); `"asc"` or `"desc"` when it is (renders a
   * single arrow pointing the current direction).
   */
  readonly direction: "asc" | "desc" | null;
  /**
   * The full Dutch accessible name describing what clicking this header does
   * next (e.g. "Sorteer op Bestuurder aflopend"), not merely the column name:
   * a screen reader user chooses whether to activate a control based on what
   * it does, and "next direction" is exactly the one thing the visible label
   * and the decorative icon do not convey between them.
   */
  readonly accessibleLabel: string;
  readonly onSort: () => void;
  readonly className?: string;
}

/**
 * First consumed by admin-v2's dossiers overview (`dossiers/dossierColumns.tsx`);
 * export through this package's barrel (`./index.ts`) and demo every state in
 * admin-v2's `routes/showcase.tsx` per the admin-v2 primitive convention.
 */
export function SortableColumnHeader({
  children,
  direction,
  accessibleLabel,
  onSort,
  className,
}: SortableColumnHeaderProps): ReactNode {
  return (
    <button
      type="button"
      onClick={onSort}
      aria-label={accessibleLabel}
      className={cn(
        "group inline-flex items-center gap-1 whitespace-nowrap uppercase tracking-wide transition-colors hover:text-foreground",
        direction === null ? "text-muted-foreground" : "text-foreground",
        className,
      )}
    >
      {children}
      {direction === null ? (
        // Subtle: only shown on hover/focus, so an inactive header does not
        // compete visually with the one active arrow.
        <ChevronsUpDown
          aria-hidden="true"
          className="size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        />
      ) : direction === "asc" ? (
        <ArrowUp aria-hidden="true" className="size-3 shrink-0" />
      ) : (
        <ArrowDown aria-hidden="true" className="size-3 shrink-0" />
      )}
    </button>
  );
}
